import { Select } from 'front-end/src/components'
import React, { useState } from 'react'
import { StringParam, useQueryParam, withDefault } from 'use-query-params'

/**
 * this simple hook is when you want to have a simple controlled Select component with an enum as values
 */
export function useControlledSelectFromEnum<
  E extends { [key: number]: string | number }
>(enu: E, defaultVal?: keyof E) {
  type optionType = {
    value: string
    label: string
  }
  const options = Object.entries(enu).map(([label, value]) => {
    return {
      value: value as string,
      label
    }
  })

  const [selected, setSelected] = useState<optionType>(
    defaultVal
      ? (options.find(({ value }) => value === defaultVal) as optionType)
      : options[0]
  )

  const c = (
    <Select
      options={options}
      value={selected}
      onChange={(o) => {
        setSelected(o as optionType)
      }}
      className="roleSelect"
    />
  )

  return {
    setSelected,
    controlledSelect: c,
    selected: selected.value as keyof E
  }
}

/**
 * similar as previous, but the selected value is kept in the URL query instead of component state
 */
export function useControlledSelectFromEnumWithUrlQuery<
  E extends { [key: number]: string | number }
>({
  enu,
  queryParamName,
  defaultValue,
  omit
}: {
  enu: E
  queryParamName: string
  defaultValue?: keyof E
  omit?: Array<keyof E>
}) {
  let options = Object.entries(enu).map(([label, value]) => {
    return {
      value: value as string,
      label
    }
  })
  if (omit) {
    options = options.filter(
      ({ value }) => omit.includes(value as keyof E) === false
    )
  }

  const [selected, setSelected] = useQueryParam(
    queryParamName,
    withDefault(StringParam, (defaultValue as string) ?? options[0].value)
  )

  const c = (
    <Select
      options={options}
      value={options.find(({ value }) => value === selected)}
      onChange={(o) => {
        setSelected(o?.value as string)
      }}
      className="roleSelect"
    />
  )

  return {
    setSelected,
    controlledSelect: c,
    selected: selected as keyof E
  }
}
